import { AccountInfo } from './components/AccountInfo/AccountInfo';
import { BusinessDetails } from './components/BusinessDetails/BusinessDetails';
import { DirectorsAndShareholders } from './components/DirectorsAndShareholders/DirectorsAndShareholders';
import { ManageAccountBlock } from './components/ManageAccountBlock/ManageAccountBlock';
import { PayLaterPricingSettings } from './components/PayLaterPricingSettings/PayLaterPricingSettings';
import { PaymentMethod } from './components/PaymentMethod/PaymentMethod';
import { SafeLink } from '../../components/SafeLink/SafeLink';
import { useUserGroups } from '../../hooks/useUserGroups';

export type TOptionLink = {
  title: string;
  onClick: () => void;
};

export const ManageAccount = () => {
  const { isStaff, isAdmin, isOwner, isEditor, isLoading } = useUserGroups();

  const isMaxPrivillage = isStaff || isOwner;
  const isMedPrivillage = isMaxPrivillage || isAdmin || isEditor;
  // coming soon - isLowPrivillage which is "Viewer"

  if (isLoading) return <></>;

  type TOption = {
    title: string;
    subtitle: JSX.Element[];
    children?: JSX.Element;
    visible: boolean;
    link?: TOptionLink;
  };

  const options: TOption[] = [
    {
      title: 'Choose who pays for Pay Later',
      subtitle: [
        <>
          These settings will apply for Universal links, integrations and API.
        </>,
        <>
          It’ll also be the default for Package and Custom links - head to the{' '}
          <SafeLink to="/pay/pay-links/custom">Pay links page</SafeLink> to make
          changes on these links.
        </>,
      ],
      children: <PayLaterPricingSettings />,
      visible: isMedPrivillage,
    },
    {
      title: 'Your profile info',
      subtitle: [
        <>Contact your account manager if you wish to change this info.</>,
      ],
      children: <AccountInfo />,
      visible: true,
    },
    {
      title: 'Your iwoca business details',
      subtitle: [
        <>This is what information we have on you and your business.</>,
      ],
      children: <BusinessDetails />,
      link: {
        title: 'Edit this info',
        onClick: () =>
          (window.location.href = `/account/company/${window.location.search}`),
      },
      visible: isMaxPrivillage,
    },
    {
      title: 'Business bank details',
      subtitle: [<>This is where we'll send your funds:</>],
      children: <PaymentMethod />,
      visible: isMaxPrivillage,
    },
    {
      title: 'Directors and shareholders',
      subtitle: [
        <>
          These are the details of all the directors and major shareholders with
          25% or more shares in your business.
        </>,
      ],
      children: <DirectorsAndShareholders />,
      visible: isMaxPrivillage,
    },
  ];

  return (
    <div className="mb-4xl max-w-2xl text-primary-10">
      <div className="text-left sm:pl-m sm:pr-m">
        <h1 className="text-4xl">Manage account</h1>
        <div className="grid grid-cols-1 gap-4xl">
          {options.map((option: TOption, index: number) => {
            if (!option.visible) return <></>;

            return (
              <ManageAccountBlock
                title={option.title}
                subtitle={option.subtitle}
                link={option.link}
                key={index}
              >
                {option.children && option.children}
              </ManageAccountBlock>
            );
          })}
        </div>
      </div>
    </div>
  );
};
